import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle, StatusPill, KpiCard } from "@/components/dashboard/widgets";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Banknote, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import StripeConnectCard from "@/components/affiliate/StripeConnectCard";

export default function Commissions() {
  const [rows, setRows] = useState([]);
  const [bal, setBal] = useState(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank_transfer");
  const [busy, setBusy] = useState(false);

  const refresh = () => Promise.all([
    api.get("/affiliate/payouts").then(r => setRows(r.data)),
    api.get("/affiliate/payouts/available").then(r => setBal(r.data)).catch(() => {}),
  ]);
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    const a = parseFloat(amount);
    if (!a || a <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post("/affiliate/payouts/request", { amount: a, method });
      toast.success(`Payout request for £${a.toFixed(2)} submitted.`);
      setOpen(false); setAmount(""); setMethod("bank_transfer");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <SectionTitle title="Commissions & payouts" subtitle="Earnings, payout history and instant withdrawal requests." />
        <Button onClick={() => setOpen(true)} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="payout-request-cta">
          <ArrowUpRight className="size-4 mr-1.5" /> Request payout
        </Button>
      </div>

      {bal && (
        <div className="grid sm:grid-cols-3 gap-4">
          <KpiCard label="Lifetime commission" value={bal.total_commission.toLocaleString("en-GB", { minimumFractionDigits: 2 })} prefix="£" testid="kpi-lifetime" />
          <KpiCard label="Paid or requested" value={bal.paid_or_requested.toLocaleString("en-GB", { minimumFractionDigits: 2 })} prefix="£" testid="kpi-paid" />
          <KpiCard label="Available to withdraw" value={bal.available.toLocaleString("en-GB", { minimumFractionDigits: 2 })} prefix="£" testid="kpi-available" />
        </div>
      )}

      <StripeConnectCard />

      <Card className="rounded-xl shadow-soft border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60"><tr className="text-xs uppercase tracking-widest text-slate-500 text-left">
                <th className="py-3 px-4 font-medium">Issued</th><th className="font-medium">Method</th><th className="font-medium">Status</th><th className="font-medium text-right pr-4">Amount</th>
              </tr></thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`payout-row-${p.id}`}>
                    <td className="py-3 px-4">{new Date(p.issued_at).toLocaleDateString("en-GB")}</td>
                    <td>{p.method}</td>
                    <td><StatusPill status={p.status} /></td>
                    <td className="text-right pr-4 font-mono tabular-nums">£{p.amount.toFixed(2)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-8 text-sm text-slate-500">No payouts yet — click <strong>Request payout</strong> to get started.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="payout-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Banknote className="size-5 text-[#0055FF]" /> Request a payout</DialogTitle>
            <DialogDescription>
              {bal ? `You have £${bal.available.toFixed(2)} available to withdraw.` : "Submit a payout request and our finance team will process it within 2 working days."}
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-1.5">
              <Label htmlFor="payout-amount">Amount (£)</Label>
              <Input id="payout-amount" data-testid="payout-amount" type="number" step="0.01" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={bal ? bal.available.toFixed(2) : "0.00"} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="payout-method">Method</Label>
              <select
                id="payout-method"
                data-testid="payout-method"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
              >
                <option value="bank_transfer">Bank Transfer (UK BACS · 1–2 days)</option>
                <option value="stripe_connect">Stripe Connect Express (instant)</option>
              </select>
              {method === "stripe_connect" && (
                <div className="text-[11.5px] text-slate-500 mt-1.5 leading-relaxed">
                  Stripe Connect onboarding link is sent to your registered email after the request is approved by our finance team.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="payout-submit" className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white">
                {busy ? <><Loader2 className="size-4 animate-spin mr-1.5" /> Submitting</> : "Submit request"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
